import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const SUBSCRIPTION_TIERS = {
  starter: {
    productId: "prod_Ta4wxOON93Fv29",
    priceId: "price_1ScumnDTyuOcf0PJnySR8VDY",
    name: "Starter",
  },
  growth: {
    productId: "prod_Ta4whr6QHNwO3d",
    priceId: "price_1ScumwDTyuOcf0PJaXxp7thH",
    name: "Growth",
  },
  enterprise: {
    productId: "prod_Ta4wRXbADLBHGz",
    priceId: "price_1ScumxDTyuOcf0PJ16u6gTRj",
    name: "Enterprise",
  },
} as const;

export type SubscriptionTier = keyof typeof SUBSCRIPTION_TIERS | null;

interface SubscriptionState {
  subscribed: boolean;
  productId: string | null;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
  loading: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    productId: null,
    tier: null,
    subscriptionEnd: null,
    loading: true,
  });

  const getTierFromProductId = (productId: string | null): SubscriptionTier => {
    if (!productId) return null;
    const entry = Object.entries(SUBSCRIPTION_TIERS).find(
      ([_, tier]) => tier.productId === productId
    );
    return entry ? (entry[0] as SubscriptionTier) : null;
  };

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setState({
        subscribed: false,
        productId: null,
        tier: null,
        subscriptionEnd: null,
        loading: false,
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");

      if (error) throw error;

      setState({
        subscribed: data?.subscribed || false,
        productId: data?.product_id || null,
        tier: getTierFromProductId(data?.product_id),
        subscriptionEnd: data?.subscription_end || null,
        loading: false,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [user]);

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
      throw error;
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Refresh subscription status periodically
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(checkSubscription, 60000); // Every minute
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  return {
    ...state,
    checkSubscription,
    openCustomerPortal,
    tierName: state.tier ? SUBSCRIPTION_TIERS[state.tier].name : null,
  };
}
